import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { ROLE_COLORS } from "../constants/roles";

const RoleContext = createContext();

// helper to get user permissions from localStorage
const getUserPermissions = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    // If no user is logged in, return null to indicate no user
    if (!user) return null;
    return {
      canDonate: user?.can_donate === 1 || user?.can_donate === true,
      canReceive: user?.can_receive === 1 || user?.can_receive === true,
    };
  } catch {
    return null;
  }
};

// provides role context (donor/receiver) and dynamically updates CSS variables for role-based theming
export function RoleProvider({ children }) {
  const [currentRole, setCurrentRole] = useState(() => {
    const savedRole = sessionStorage.getItem("userRole");
    const permissions = getUserPermissions();

    // if no user is logged in, default to donor (green theme)
    if (!permissions) return "donor";

    const { canDonate, canReceive } = permissions;

    // if user only has one role, use that
    if (canDonate && !canReceive) return "donor";
    if (canReceive && !canDonate) return "receiver";

    // otherwise use saved role or default to donor
    return savedRole || "donor";
  });

  // function to refresh role based on current permissions
  const refreshRole = useCallback(() => {
    const permissions = getUserPermissions();

    // if no user is logged in, reset to donor (green theme for login page)
    if (!permissions) {
      setCurrentRole("donor");
      return;
    }

    const { canDonate, canReceive } = permissions;

    // if user only has one role, force them to it
    if (canDonate && !canReceive) {
      setCurrentRole("donor");
    } else if (canReceive && !canDonate) {
      setCurrentRole("receiver");
    }
  }, []);

  // listen for storage changes (when user logs in/out)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "user") {
        refreshRole();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [refreshRole]);

  // check permissions on mount and when role changes
  useEffect(() => {
    refreshRole();
  }, [refreshRole]);

  useEffect(() => {
    sessionStorage.setItem("userRole", currentRole);
  }, [currentRole]);

  // update CSS variables when role changes
  useEffect(() => {
    const root = document.documentElement;
    const colors = ROLE_COLORS[currentRole];

    root.style.setProperty("--role-accent", colors.primary);
    root.style.setProperty("--role-accent-rgb", colors.primaryRgb);
    root.style.setProperty("--role-accent-hover", colors.primary);
    root.style.setProperty("--role-accent-light", colors.accentLight);
    root.style.setProperty("--role-accent-dark", colors.accentDark);
  }, [currentRole]);

  const switchRole = (newRole) => {
    const { canDonate, canReceive } = getUserPermissions();

    // only allow switching if user has permission for the new role
    if (newRole === "donor" && !canDonate) return;
    if (newRole === "receiver" && !canReceive) return;

    setCurrentRole(newRole);
  };

  return (
    <RoleContext.Provider value={{ currentRole, switchRole, refreshRole }}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error("useRole must be used within RoleProvider");
  }
  return context;
}
